import usePlacesAutocomplete, {
  getGeocode,
  getZipCode,
  getDetails,
} from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";
import { emitCustomEvent } from "react-custom-events";
import { useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

const defaultTypes = ["address"];
const PlacesAutocomplete = ({
  darmo,
  label,
  pValue,
  setAddressCheck,
  error,
  isAddressSearch = false,
  type,
}) => {
  const { t } = useTranslation();
  const searchPara = isAddressSearch ? defaultTypes : [];
  const inputRef = useRef(null);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
      componentRestrictions: { country: "us" },
      types: [...searchPara],
    },
    debounce: 300,
  });

  useEffect(() => {
    setValue(pValue, false);
  }, [pValue]);

  const ref = useOnclickOutside(() => {
    // When user clicks outside of the component, we can dismiss
    // the searched suggestions by calling this method
    clearSuggestions();
  });

  const handleInput = (e) => {
    // Update the keyword of the input element
    if (setAddressCheck) {
      setAddressCheck(e);
    }
    setValue(e.target.value);
  };

  const handleSelect =
    ({ description, terms }) =>
    () => {
      let addressLine = "";

      for (let i = 0; i < terms.length - 3; i++) {
        addressLine += terms[i].value + (i !== terms.length - 4 ? ", " : "");
      }
      setValue(addressLine, false);
      clearSuggestions();

      // Get latitude and longitude via utility functions
      getGeocode({ address: description })
        .then((results) => {
          getZipCode(results[0], false).then(async (result) => {
            const parameter = {
              placeId: results[0].place_id,
              fields: ["formatted_phone_number", "name", "website", "types"],
            };
            const details = await getDetails(parameter);
            emitCustomEvent("placeSelected", {
              description: results[0].formatted_address,
              addressLine: addressLine,
              terms: terms,
              zipCode: result,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
              business_number: details.formatted_phone_number,
              business_name: details.name,
              website: details.website,
              categories: details.types,
            });
          });
        })
        .catch((error) => {
          toast.error(t("errors.something-is-went-wrong"));
        });
    };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
        terms,
      } = suggestion;
      return (
        <li
          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
          key={place_id}
          onClick={handleSelect(suggestion, terms)}
        >
          <span className="font-medium">{main_text}</span>
          <span className="text-gray-500 ml-1">{secondary_text}</span>
        </li>
      );
    });

  return (
    <div ref={ref} className="relative">
      <label className="block font-bold title text-warmgray-900">{label}</label>
      <div className={`flex flex-row border w-full focus:outline-none focus-within:border-violet-400 ${error ? "border-red-500" : ""} ${!ready ? "bg-gray-50" : ""}`}>
        <input
          ref={inputRef}
          value={value}
          onChange={handleInput}
          disabled={!ready}
          placeholder={label}
          className={`w-full focus:outline-none flex border border-warmgray-300 rounded-md p-2 ${!ready ? "bg-gray-50" : ""}`}
          type={type}
        />
      </div>
      {error && (
        <span className="block text-red-500">
          <small>{error}</small>
        </span>
      )}
      {/* Suggestions dropdown */}
      {status === "OK" && (
        <ul className="z-[999] absolute bg-white border border-warmgray-300 w-full shadow-md mt-1 rounded-md">
          {renderSuggestions()}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
